import { useEffect, useRef, useState } from "react";
import { api, imageUrl, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/LangContext";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UploadSimple, X, Star, Translate } from "@phosphor-icons/react";

const TYPES = ["Building", "Monument", "Site"];
const CONDITIONS = ["Excellent", "Good", "Fair", "Poor", "Critical"];
const STATUSES = ["Gazetted", "Director List", "Not Listed"];
const AUTHORITIES = ["DBKU", "MBKS", "Bau District Council"];

const EMPTY = {
  name: "",
  type: "Building",
  description: "",
  historical_significance: "",
  location: "",
  latitude: 1.5555,
  longitude: 110.3452,
  owner: "",
  managed_by: "",
  local_authority: "DBKU",
  year_built: "",
  condition: "Good",
  status: "Not Listed",
  gazette_no: "",
  land_status: "",
  image_urls: [],
  name_ms: "",
  description_ms: "",
  historical_significance_ms: "",
  location_ms: "",
  owner_ms: "",
  managed_by_ms: "",
};

export default function HeritageForm({ open, onOpenChange, initial, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [translating, setTranslating] = useState(false);
  const fileRef = useRef(null);
  const isEdit = !!initial;

  useEffect(() => {
    if (open) {
      if (initial) {
        const urls =
          initial.image_urls && initial.image_urls.length > 0
            ? initial.image_urls
            : initial.image_url
            ? [initial.image_url]
            : [];
        setForm({
          ...EMPTY,
          ...initial,
          image_urls: urls,
          year_built: initial.year_built ?? "",
          owner: initial.owner || "",
          managed_by: initial.managed_by || "",
          name_ms: initial.name_ms || "",
          description_ms: initial.description_ms || "",
          historical_significance_ms: initial.historical_significance_ms || "",
          location_ms: initial.location_ms || "",
          owner_ms: initial.owner_ms || "",
          managed_by_ms: initial.managed_by_ms || "",
        });
      } else {
        setForm(EMPTY);
      }
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data.url);
      }
      setForm((f) => ({ ...f, image_urls: [...f.image_urls, ...uploaded] }));
      toast.success(
        t(uploaded.length > 1 ? "toast.upload.plural" : "toast.upload.success", { n: uploaded.length })
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("toast.upload.failed"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeImage = (idx) => {
    setForm((f) => ({ ...f, image_urls: f.image_urls.filter((_, i) => i !== idx) }));
  };

  const makeCover = (idx) => {
    setForm((f) => {
      const arr = [...f.image_urls];
      const [pick] = arr.splice(idx, 1);
      arr.unshift(pick);
      return { ...f, image_urls: arr };
    });
  };

  const autoTranslate = async () => {
    const fields = {
      name: form.name,
      description: form.description,
      historical_significance: form.historical_significance,
      location: form.location,
      owner: form.owner,
      managed_by: form.managed_by,
    };
    const nonEmpty = Object.fromEntries(
      Object.entries(fields).filter(([, v]) => v && v.trim().length > 0)
    );
    if (Object.keys(nonEmpty).length === 0) {
      toast.error("Add some English text first, then translate.");
      return;
    }
    setTranslating(true);
    try {
      const { data } = await api.post("/translate", { fields: nonEmpty });
      setForm((f) => ({ ...f, ...data }));
      toast.success(t("form.translate.success", { n: Object.keys(data).length }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("form.translate.failed"));
    } finally {
      setTranslating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error(t("form.err.name"));
    const payload = {
      ...form,
      image_url: form.image_urls[0] || null,
      latitude: parseFloat(form.latitude),
      longitude: parseFloat(form.longitude),
      year_built: form.year_built === "" ? null : parseInt(form.year_built, 10),
    };
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/heritage/${initial.id}`, payload);
        toast.success(t("toast.saved.edit"));
      } else {
        await api.post("/heritage", payload);
        toast.success(t("toast.saved.new"));
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "rounded-none border-[#e5e3db] focus:border-[#b25a38] focus:ring-1 focus:ring-[#b25a38]";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl bg-white border border-[#e5e3db] rounded-none max-h-[90vh] overflow-y-auto"
        data-testid="heritage-form-dialog"
      >
        <DialogHeader>
          <div className="overline">{isEdit ? t("form.edit") : t("form.new")}</div>
          <DialogTitle className="serif text-3xl font-bold text-[#1a1a1a]">
            {isEdit ? form.name || t("form.title.default") : t("form.title.new")}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#5c5c5c]">
            {t("form.subtitle")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-2">
          {/* Photograph gallery */}
          <div>
            <Label className="overline block mb-2">{t("form.gallery")}</Label>
            <div className="border border-[#e5e3db] bg-[#faf8f4] p-4">
              {form.image_urls.length === 0 ? (
                <p className="text-xs text-[#5c5c5c] italic mb-3">{t("form.gallery.empty")}</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3" data-testid="form-image-grid">
                  {form.image_urls.map((url, idx) => (
                    <div
                      key={url + idx}
                      className="relative aspect-[4/3] border border-[#e5e3db] bg-white overflow-hidden group"
                      data-testid={`form-image-${idx}`}
                    >
                      <img src={imageUrl(url)} alt={`upload-${idx}`} className="w-full h-full object-cover" />
                      {idx === 0 && (
                        <span className="absolute top-1 left-1 bg-[#b25a38] text-white text-[9px] uppercase tracking-[0.18em] font-semibold px-1.5 py-0.5">
                          {t("form.cover")}
                        </span>
                      )}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex items-center justify-center gap-2">
                        {idx !== 0 && (
                          <button
                            type="button"
                            onClick={() => makeCover(idx)}
                            className="h-7 w-7 bg-white text-[#1a1a1a] flex items-center justify-center hover:bg-[#b25a38] hover:text-white"
                            aria-label="Make cover"
                            data-testid={`form-image-cover-${idx}`}
                          >
                            <Star size={12} weight="duotone" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          className="h-7 w-7 bg-white text-[#1a1a1a] flex items-center justify-center hover:bg-[#d32f2f] hover:text-white"
                          aria-label="Remove"
                          data-testid={`form-image-remove-${idx}`}
                        >
                          <X size={12} weight="bold" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-3">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleUpload}
                  className="hidden"
                  data-testid="heritage-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="rounded-none border-[#e5e3db] hover:bg-[#1a1a1a] hover:text-white hover:border-[#1a1a1a]"
                  data-testid="heritage-upload-btn"
                >
                  <UploadSimple size={16} weight="duotone" className="mr-2" />
                  {uploading ? t("form.upload.progress") : t("form.upload")}
                </Button>
                <p className="text-xs text-[#5c5c5c]">{t("form.upload.hint")}</p>
              </div>
            </div>
          </div>

          {/* Row: Name & Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="overline block mb-2">{t("form.field.name")}</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="heritage-name-input" required />
            </div>
            <div>
              <Label className="overline block mb-2">{t("form.field.type")}</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger className="rounded-none border-[#e5e3db]" data-testid="heritage-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map((tp) => <SelectItem key={tp} value={tp}>{tp}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="overline block mb-2">{t("form.field.description")}</Label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className={inputCls} data-testid="heritage-description-input" />
          </div>

          <div>
            <Label className="overline block mb-2">{t("form.field.significance")}</Label>
            <Textarea value={form.historical_significance} onChange={(e) => set("historical_significance", e.target.value)} rows={2} className={inputCls} data-testid="heritage-significance-input" />
          </div>

          {/* Location & GPS */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="overline block mb-2">{t("form.field.location")}</Label>
              <Input value={form.location} onChange={(e) => set("location", e.target.value)} className={inputCls} placeholder={t("form.field.location.placeholder")} data-testid="heritage-location-input" />
            </div>
            <div>
              <Label className="overline block mb-2">{t("form.field.lat")}</Label>
              <Input type="number" step="0.000001" value={form.latitude} onChange={(e) => set("latitude", e.target.value)} className={inputCls} data-testid="heritage-lat-input" required />
            </div>
            <div>
              <Label className="overline block mb-2">{t("form.field.lng")}</Label>
              <Input type="number" step="0.000001" value={form.longitude} onChange={(e) => set("longitude", e.target.value)} className={inputCls} data-testid="heritage-lng-input" required />
            </div>
          </div>

          {/* Managed / Authority / Year / Condition */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="overline block mb-2">{t("form.field.owner")}</Label>
              <Input value={form.owner} onChange={(e) => set("owner", e.target.value)} className={inputCls} data-testid="heritage-owner-input" />
            </div>
            <div>
              <Label className="overline block mb-2">{t("form.field.managed")}</Label>
              <Input value={form.managed_by} onChange={(e) => set("managed_by", e.target.value)} className={inputCls} data-testid="heritage-managed-input" />
            </div>
            <div>
              <Label className="overline block mb-2">{t("form.field.authority")}</Label>
              <Select value={form.local_authority} onValueChange={(v) => set("local_authority", v)}>
                <SelectTrigger className="rounded-none border-[#e5e3db]" data-testid="heritage-authority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{AUTHORITIES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline block mb-2">{t("form.field.year")}</Label>
              <Input type="number" value={form.year_built} onChange={(e) => set("year_built", e.target.value)} className={inputCls} placeholder={t("form.field.year.placeholder")} data-testid="heritage-year-input" />
            </div>
            <div>
              <Label className="overline block mb-2">{t("form.field.condition")}</Label>
              <Select value={form.condition} onValueChange={(v) => set("condition", v)}>
                <SelectTrigger className="rounded-none border-[#e5e3db]" data-testid="heritage-condition-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {/* Status / Gazette / Land Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="overline block mb-2">{t("form.field.status")}</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="rounded-none border-[#e5e3db]" data-testid="heritage-status-select">
                  <SelectValue placeholder={t("form.field.status.select")} />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{t(`status.${s}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.status === "Gazetted" && (
              <div data-testid="heritage-gazette-wrapper">
                <Label className="overline block mb-2">{t("form.field.gazette_no")}</Label>
                <Input
                  value={form.gazette_no}
                  onChange={(e) => set("gazette_no", e.target.value)}
                  className={inputCls}
                  placeholder={t("form.field.gazette_no.placeholder")}
                  data-testid="heritage-gazette-input"
                />
              </div>
            )}
            <div className={form.status === "Gazetted" ? "md:col-span-2" : ""}>
              <Label className="overline block mb-2">{t("form.field.land_status")}</Label>
              <Input
                value={form.land_status}
                onChange={(e) => set("land_status", e.target.value)}
                className={inputCls}
                placeholder={t("form.field.land_status.placeholder")}
                data-testid="heritage-land-status-input"
              />
            </div>
          </div>

          {/* Bahasa Malaysia section */}
          <div className="border-t border-[#e5e3db] pt-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="overline">{t("form.section.ms")}</div>
                <p className="text-xs text-[#5c5c5c] mt-1 max-w-lg">{t("form.section.ms.hint")}</p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={autoTranslate}
                disabled={translating}
                className="rounded-none border-[#b25a38] text-[#b25a38] hover:bg-[#b25a38] hover:text-white flex-shrink-0"
                data-testid="translate-btn"
              >
                <Translate size={16} weight="duotone" className="mr-2" />
                {translating ? t("form.translate.progress") : t("form.translate")}
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="overline block mb-2">{t("form.field.name.ms")}</Label>
                <Input value={form.name_ms} onChange={(e) => set("name_ms", e.target.value)} className={inputCls} data-testid="heritage-name-ms-input" />
              </div>
              <div>
                <Label className="overline block mb-2">{t("form.field.location.ms")}</Label>
                <Input value={form.location_ms} onChange={(e) => set("location_ms", e.target.value)} className={inputCls} data-testid="heritage-location-ms-input" />
              </div>
              <div className="md:col-span-2">
                <Label className="overline block mb-2">{t("form.field.description.ms")}</Label>
                <Textarea value={form.description_ms} onChange={(e) => set("description_ms", e.target.value)} rows={3} className={inputCls} data-testid="heritage-description-ms-input" />
              </div>
              <div className="md:col-span-2">
                <Label className="overline block mb-2">{t("form.field.significance.ms")}</Label>
                <Textarea value={form.historical_significance_ms} onChange={(e) => set("historical_significance_ms", e.target.value)} rows={2} className={inputCls} data-testid="heritage-significance-ms-input" />
              </div>
              <div>
                <Label className="overline block mb-2">{t("form.field.owner.ms")}</Label>
                <Input value={form.owner_ms} onChange={(e) => set("owner_ms", e.target.value)} className={inputCls} data-testid="heritage-owner-ms-input" />
              </div>
              <div>
                <Label className="overline block mb-2">{t("form.field.managed.ms")}</Label>
                <Input value={form.managed_by_ms} onChange={(e) => set("managed_by_ms", e.target.value)} className={inputCls} data-testid="heritage-managed-ms-input" />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#e5e3db]">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-none border-[#e5e3db] hover:bg-[#f9f8f6]"
              data-testid="heritage-cancel-btn"
            >
              {t("form.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={saving || uploading || translating}
              className="rounded-none bg-[#b25a38] hover:bg-[#93492c] text-white"
              data-testid="heritage-save-btn"
            >
              {saving ? t("form.saving") : isEdit ? t("form.save.edit") : t("form.save.new")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
